import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import Plans from './Plans'
import { sendAnalyticsEvents } from '../../analytics/common'
import { EVENTS } from '../../analytics/events'
import { useDispatch, useSelector } from 'react-redux'
import { selectEventsData, setEventDataList } from '../events/eventsSlice'
import { selectPlan, selectIfCheckoutVisited, setShowCheckout } from './plansSlice'
import { selectName } from '../family/familySlice'
import { fontSize, wh, tabletMinWidth } from '../../mixins'
import TimerShort from '../../UI/TimerShort'
import UIButton from '../../UI/UIButton'
import { Forbes, TechCrunch, Mashable } from '../family/logos'
import { PLANS } from '../../analytics/eCommerce'
import {
  selectEmail,
  selectSuccess,
  selectUserUuid,
  selectValidity,
  sendAPIRequest,
  selectPaymentSettingsStatus,
} from '../signup/signupSlice';
import SignupInput from '../signup/SignupInput';
import RedditPixel from 'react-reddit-pixel';
import {setLoader} from "../loader/loaderSlice";

const isArabic = navigator.language.startsWith('ar')
const textAlign = isArabic ? 'right' : 'left'

const Title = styled.h1`
  font-weight: 600;
  ${fontSize(32)};
  line-height: 38px;
  color: #323232;
  text-align: center;
  margin: 40px 0 24px 0;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  @media screen and (min-width: ${tabletMinWidth}px) {
    margin: 102px 0 12px 0;
  }
`

const PlansWrapper = styled.div`
  padding: 0 12px;
  ${({ paddingTop }) => paddingTop && `padding-top: ${paddingTop}px`};
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 42px;

  @media screen and (min-width: ${tabletMinWidth}px) {
    margin-bottom: 76px;
  }
`

const SecondaryTitle = styled.h2`
  font-weight: 600;
  ${fontSize(20)};
  line-height: 27px;
  color: #323232;
  text-align: center;
  margin: 48px 0 16px;

  @media screen and (min-width: ${tabletMinWidth}px) {
    ${fontSize(32)};
    line-height: 38px;
    margin: 64px 0  24px;
  }
`

const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 24px 0;

  @media screen and (min-width: 600px) {
    display: flex;
    flex-wrap: wrap;
    max-width: 550px;
  }
`

const StyledItem = styled.li`
  position: relative;
  padding-${textAlign}: 20px;
  font-weight: 400;
  ${fontSize(16)};
  line-height: 20px;
  color: #323232;
  text-align: ${textAlign};

  @media screen and (min-width: 600px) {
    width: 50%;
  }
  
  svg {
    ${wh('12px')};
    position: absolute;
    top: 4px;
    ${textAlign}: 0;
  }
  
  &:not(:last-child) {
    margin-bottom: 8px;
  }
`

const PressLogos = styled.div`
  display: flex;
  flex-direction: revert;
  justify-content: center;
  flex-wrap: wrap;

  @media screen and (min-width: ${tabletMinWidth}px) {
    flex-wrap: nowrap;
    align-items: center;
    zoom: 2;
  }

  svg {
    width: 40%;

    &:not(:last-child) {
      margin-bottom: 26px;

      @media screen and (min-width: ${tabletMinWidth}px) {
        margin-bottom: 0;
        margin-right: 20px;
      }
    }
  }
`

const ReviewsWrapper = styled.div`
  max-width: 327px;
  margin-bottom: 24px;

  @media screen and (min-width: 1280px) {
    max-width: initial;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const ReviewItem = styled.div`
  background: #f4f4f4;
  border-radius: 13px;
  padding: 12px 16px;
  max-width: 327px;
  align-self: stretch;

  &:not(:last-child) {
    margin-bottom: 8px;

    @media screen and (min-width: 1280px) {
      margin-bottom: 0;
      margin-right: 16px;
    }
  }
`

const ReviewHead = styled.div`
  display: flex;
  flex-direction: row${isArabic ? '-reverse' : ''};
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 8px;
`

const ReviewBody = styled.div`
  display: flex;
  flex-direction: row${isArabic ? '-reverse' : ''};
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`

const ReviewerPhoto = styled.img`
  width: 30px;
  height: 30px;
`

const ReviewerName = styled.span`
  font-weight: 690;
  font-size: 14px;
  line-height: 20px;
  color: #323232;
`

const UserIcon = styled.div`
  ${wh('14px')};
  background-image: url('../assets/app-icon.png');
  background-size: 100%;
  background-repeat: no-repeat;
  background-position: center;
`

const Label = styled.div`
  background: #e9e9e9;
  border-radius: 2px;
  padding: 2px;
  margin: 0 7px;
  font-weight: 600;
  ${fontSize(6)};
  line-height: 7px;
  letter-spacing: -0.24px;
  text-transform: capitalize;
  color: #5b5b5b;
`

const Rate = styled.div`
  ${wh('53px', '10px')};
  background-image: url('../assets/stars.png');
  background-size: 100%;
  background-repeat: no-repeat;
  background-position: center;
`

const ReviewText = styled.div`
  font-weight: 400;
  ${fontSize(14)};
  line-height: 17px;
  letter-spacing: -0.24px;
  text-align: ${textAlign};
`

const OnePagePlans = ({ history }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const plan = useSelector(selectPlan)
  const eventsData = useSelector(selectEventsData)
  const signUpSuccess = useSelector(selectSuccess)
  const email = useSelector(selectEmail);
  const userName = useSelector(selectName)
  const userUuid = useSelector(selectUserUuid)
  const isEmailValid = useSelector(selectValidity)
  const paymentSettingsStatus = useSelector(selectPaymentSettingsStatus)
  const isCheckoutVisited = useSelector(selectIfCheckoutVisited)

  const FEATURES = [
    t('know_when_family_come_or_go'),
    t('realtime_location_sharing'),
    t('find_loved_ones'),
    t('be_notified_in_case'),
    t('get_notified_when_the_land'),
  ]

  const renderItem = (i, index) => {
    return (
      <StyledItem key={index}>
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5.53828 13C5.83759 13 6.07425 12.8666 6.2413 12.6068L12.819 2.15857C12.9443 1.95494 13 1.80047 13 1.63897C13 1.25278 12.7425 1 12.3596 1C12.0882 1 11.9281 1.09128 11.761 1.3581L5.51044 11.4061L2.26682 7.12288C2.09281 6.87712 1.92575 6.77882 1.66821 6.77882C1.27842 6.77882 1 7.05266 1 7.43885C1 7.60035 1.06961 7.78291 1.20186 7.95143L4.81439 12.5927C5.03016 12.8666 5.23898 13 5.53828 13Z"
            fill="#3393EC"
            stroke="#3393EC"
          />
        </svg>
        {i}
      </StyledItem>
    )
  }

  const REVIEWS = [
    {
      market: 'IOS',
      reviewer: 'Kevin Peters',
      reviewerSource: '../assets/reviewer1.png',
      text: t('review_1'),
    },
    {
      market: 'Play',
      reviewer: 'John Lee',
      reviewerSource: '../assets/reviewer2.png',
      text: t('review_2'),
    },
    {
      market: 'Play',
      reviewer: 'Megan Smith',
      reviewerSource: '../assets/reviewer3.png',
      text: t('review_3'),
    },
  ]

  const renderReview = (r, i) => {
    return (
      <ReviewItem key={i}>
        <ReviewHead>
          <UserIcon />
          <Label>{r.market}</Label>
          <Rate />
        </ReviewHead>
        <ReviewBody>
          <ReviewerPhoto src={r.reviewerSource} alt={r.reviewer}/>
          <ReviewerName>{r.reviewer}</ReviewerName>
        </ReviewBody>
        <ReviewText>{r.text}</ReviewText>
      </ReviewItem>
    )
  }

  const onContinueClick = () => {
    const { price, duration, description, periodPrice } = PLANS[plan]
    const planData = {
      Duration: duration,
      Value: price,
      description: description,
      periodPrice: periodPrice,
    }
    dispatch(setEventDataList(planData))

    if (!signUpSuccess) {
      /*sendAnalyticsEvents(EVENTS.onboardingEnterEmailSubmitted, {
        ...eventsData,
        ...planData,
      });*/
      sendAnalyticsEvents(EVENTS.onboardingPaywallChosen, {
        ...eventsData,
        ...planData,
      })
      dispatch(sendAPIRequest(email, userName, userUuid))
    } else {
      sendAnalyticsEvents(EVENTS.onboardingPaywallChosen, {
        ...eventsData,
        ...planData,
      })
      history.push('/checkout')
      dispatch(setShowCheckout(true))
    }
  }

  useEffect(() => {
    if (signUpSuccess) {
      RedditPixel.track('SignUp', {'user_email': email});
      sendAnalyticsEvents(EVENTS.signupSuccess, eventsData);
      !isCheckoutVisited && dispatch(setLoader(true));
    }
  }, [signUpSuccess, eventsData, email, dispatch, isCheckoutVisited]);

  useEffect(() => {
    if (!isCheckoutVisited && paymentSettingsStatus === 'success') {
      history.push('/checkout');
      dispatch(setLoader(false));
    }
  }, [isCheckoutVisited, paymentSettingsStatus, history, dispatch]);

  return (
    <PlansWrapper id={'plans-wrapper'}>
      <Title onePage>{t('choose_your_plan')}</Title>
      <TimerShort />
      <Plans onePage />
      {!signUpSuccess && <SignupInput onePage/>}
      <UIButton onClick={onContinueClick} bottomPosition={'24px'} displayStickyBtn={false} customMargin={'24px 0 0'} disabled={!isEmailValid}>
        {t('get_my_plan')}
      </UIButton>
      <SecondaryTitle>{t('what_you_get')}:</SecondaryTitle>
      <StyledList>{FEATURES.map(renderItem)}</StyledList>
      <SecondaryTitle>{t('as_seen_on')}</SecondaryTitle>
      <PressLogos>
        {Forbes}
        {TechCrunch}
        {Mashable}
      </PressLogos>
      <SecondaryTitle>{t('customer_reviews')}</SecondaryTitle>
      <ReviewsWrapper>{REVIEWS.map(renderReview)}</ReviewsWrapper>
      <UIButton onClick={onContinueClick} bottomPosition={'24px'} displayStickyBtn={false} disabled={!isEmailValid}>
        {t('start_tracking')}
      </UIButton>
    </PlansWrapper>
  )
}

export default OnePagePlans
